import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Snackbar,
  Alert,
  AlertColor,
} from "@mui/material";
import PageTitle from "../../molecules/PageTitle";
import { BaseSelectionModal } from "./BaseSelectionModal";
import { useNavigate } from "react-router-dom";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { CustomContainer } from "../../../styles/styles";
import RequiredLabel from "../../atoms/RequiredLabel";
import ConfirmDialog from "../../molecules/ConfirmDialog";

const BuildingRegistrationPage: React.FC = () => {
  const navigate = useNavigate();

  const [buildingId, setBuildingId] = useState("");
  const [buildingManagementId, setBuildingManagementId] = useState("");

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [fulltechOfficeName, setFulltechOfficeName] = useState("");

  const [fulltechOfficeId, setFulltechOfficeId] = useState<number>();

  const [baseModalOpen, setBaseModalOpen] = useState(false);
  const [icmSipEndpoint, setIcmSipEndpoint] = useState<string | null>(""); // インターホンSIPエンドポイントを追加
  const [memo, setMemo] = useState("");

  // アラート
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    // window.location.reload();//下部アラートが消えた後に画面リロード
  };

  // 確認ダイアログ
  const [openConfirm, setOpenConfirm] = React.useState(false);

  // 各フィールドが変更されたかどうかを追跡する状態
  const [isTouched, setIsTouched] = useState({
    buildingManagementId: false,
    name: false,
    address: false,
    fulltechOfficeId: false,
    memo: false,
  });

  const CHARACTER_LIMIT = 50; // 文字数制限を定義

  const [validationErrors, setValidationErrors] = useState({
    buildingManagementId: "",
    name: "",
    address: "",
    fulltechOfficeId: "",
    memo: "",
  });

  // 入力が変更されたときのハンドラ
  const handleBlur = (field: string) => {
    setIsTouched({
      ...isTouched,
      [field]: true,
    });
  };

  useEffect(() => {
    const errors = {
      buildingManagementId:
        isTouched.buildingManagementId &&
        buildingManagementId !== "" &&
        buildingManagementId.length > CHARACTER_LIMIT
          ? `取付先コードは${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      name:
        isTouched.name && name !== "" && name.length > CHARACTER_LIMIT
          ? `取付先名（物件名称）は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      address:
        isTouched.address && address !== "" && address.length > CHARACTER_LIMIT
          ? `物件住所は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      fulltechOfficeId:
        isTouched.fulltechOfficeId && !fulltechOfficeId
          ? "管轄拠点を選択してください"
          : "",
      memo:
        isTouched.memo && memo !== "" && memo.length > CHARACTER_LIMIT
          ? `メモは${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
    };

    setValidationErrors(errors);
  }, [buildingManagementId, name, address, memo, isTouched, fulltechOfficeId]);

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault(); //サブミット後の更新をやめる

    if (!name || !address || !fulltechOfficeId) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        name: !name ? "取付先名（物件名称）は必須です" : prevErrors.name,
        address: !address ? "物件住所は必須です" : prevErrors.address,
        fulltechOfficeId: !fulltechOfficeId
          ? "管轄拠点を選択してください"
          : prevErrors.fulltechOfficeId,
      }));
      return; // エラーがあるため、保存操作を中断
    }

    // エラーがある場合、保存を阻止
    if (Object.values(validationErrors).some((error) => error !== "")) {
      setSnackbarMessage("入力エラーがあります。修正してください。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();
    event.preventDefault();

    // APIのURLを定義します。
    const url = process.env.REACT_APP_API_ENDPOINT + "/buildings";

    // fetchに渡すオプションを定義します。
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: jwtToken,
      },
      body: JSON.stringify({
        buildingManagementId: buildingManagementId,
        buildingName: name,
        buildingAddress: address,
        autoFulltechOfficeId: fulltechOfficeId,
        icmSipEndpoint: icmSipEndpoint, // インターホンSIPエンドポイントを追加
        memo: memo,
      }),
    };

    try {
      // call storeBuilding API
      const response = await fetch(url, options);

      if (response.ok) {
        const data = await response.json();
        setBuildingId(data.buildingId);
        console.log("レスポンス" + JSON.stringify(data));
        setOpenConfirm(true);
      } else {
        // レスポンスが異常であればエラーをスローします。
        setSnackbarMessage("物件の登録に失敗しました。");
        setAlertSeverity("error");
        setSnackbarOpen(true);
        throw new Error("Something went wrong");
      }
    } catch (error) {
      // エラーをキャッチしてログに表示します。
      setSnackbarMessage("物件の登録に失敗しました。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      console.error("Error:", error);
    }
  };

  const handleCancelClick = () => {
    console.log(`キャンセルがクリックされました`);
    navigate(-1);
  };

  return (
    <CustomContainer maxWidth={false}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PageTitle title={"物件新規登録"} />
        </Grid>
        <Grid item xs={12}>
          <form onSubmit={handleFormSubmit}>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>物件システムID:</TableCell>
                    <TableCell>
                      <Typography>{buildingId}</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>取付先コード:</TableCell>
                    <TableCell>
                      <TextField
                        label="取付先コード"
                        value={buildingManagementId}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) =>
                          setBuildingManagementId(e.target.value)
                        }
                        onBlur={() => handleBlur("buildingManagementId")}
                        helperText={validationErrors.buildingManagementId}
                        error={validationErrors.buildingManagementId !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      取付先名（物件名称）:
                      <RequiredLabel />
                    </TableCell>
                    <TableCell>
                      <TextField
                        label="取付先名（物件名称）"
                        value={name}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={() => handleBlur("name")}
                        helperText={validationErrors.name}
                        error={validationErrors.name !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      物件住所:
                      <RequiredLabel />
                    </TableCell>
                    <TableCell>
                      <TextField
                        label="物件住所"
                        value={address}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setAddress(e.target.value)}
                        onBlur={() => handleBlur("address")}
                        helperText={validationErrors.address}
                        error={validationErrors.address !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      管轄拠点:
                      <RequiredLabel />
                    </TableCell>
                    <TableCell>
                      <Box display="flex">
                        <Typography display={"flex"} alignItems={"center"}>
                          {fulltechOfficeName}
                        </Typography>
                        <Button
                          onClick={() => {
                            setBaseModalOpen(true);
                          }}
                        >
                          選択
                        </Button>
                        <BaseSelectionModal
                          baseModalOpen={baseModalOpen}
                          setBaseModalOpen={setBaseModalOpen}
                          setFulltechOfficeName={setFulltechOfficeName}
                          setFulltechOfficeId={setFulltechOfficeId}
                        />
                      </Box>
                      {validationErrors.fulltechOfficeId && (
                        <Typography color="error" variant="body2">
                          {validationErrors.fulltechOfficeId}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>インターホンSIPエンドポイント:</TableCell>
                    <TableCell>
                      <TextField
                        label="インターホンSIPエンドポイント"
                        value={icmSipEndpoint || ""}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setIcmSipEndpoint(e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>メモ:</TableCell>
                    <TableCell>
                      <TextField
                        label="メモ"
                        multiline
                        rows={3}
                        value={memo}
                        sx={{ width: "600px" }}
                        onChange={(e) => setMemo(e.target.value)}
                        onBlur={() => handleBlur("memo")}
                        helperText={validationErrors.memo}
                        error={validationErrors.memo !== ""}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Grid
              item
              xs={12}
              sm={12}
              display="flex"
              justifyContent="flex-end"
              marginTop={2}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancelClick}
              >
                キャンセル
              </Button>
              <Button
                type="submit"
                variant="outlined"
                color="primary"
                sx={{ marginLeft: 1 }}
              >
                登録
              </Button>
            </Grid>
          </form>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={openConfirm}
        onClose={() => {
          setOpenConfirm(false);
        }}
        onConfirm={() => {
          navigate(`/buildings`);
        }}
        title="確認"
        description="物件の登録が完了しました。"
        showCancelButton={false}
      />
    </CustomContainer>
  );
};

export default BuildingRegistrationPage;
